import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Plus, Trash2, Star, Eye, EyeOff, MessageCircle, BadgeCheck } from "lucide-react";
import { DICT } from "@/i18n/dict";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang, LANGS } from "@/context/LangContext";
import { useApi, http, idr, formatErr, waLink } from "@/lib/api";
import { PageHero } from "@/components/site/SectionHead";
import { STATUS_CLS } from "@/pages/MyBookings";
import { ADDON_KEYS } from "@/components/site/BookingExtras";
import PickupList from "@/components/admin/PickupList";
import AssignPartner from "@/components/admin/AssignPartner";
import ChatInbox from "@/components/admin/ChatInbox";
import PartnerApps from "@/components/admin/PartnerApps";
import CityWaitlist from "@/components/admin/CityWaitlist";
import CityPricing from "@/components/admin/CityPricing";

const STATUSES = ["pending", "confirmed", "completed", "cancelled"];
const DIAL = { ID: "62", MY: "60", SG: "65", BN: "673", GB: "44", US: "1", CA: "1", AU: "61", NZ: "64", FR: "33", BE: "32", CH: "41", DE: "49", AT: "43", NL: "31" };
const waPhone = (p, country = "ID") => {
  const d = p.replace(/\D/g, "");
  const code = DIAL[country] || "62";
  if (p.trim().startsWith("+") || d.startsWith(code)) return d;
  return code + (d.startsWith("0") ? d.slice(1) : d);
};

function AskReview({ b, onDone, tid = "admin-ask-review" }) {
  const { t } = useLang();
  if (b.reviewed) return <span data-testid={`admin-reviewed-${b.code}`} className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700"><BadgeCheck className="h-4 w-4" />{t("reviewed")}</span>;
  const ask = async () => {
    const win = window.open("", "_blank");
    try {
      const { data } = await http.post(`/admin/bookings/${b.id}/review-link`);
      const d = DICT[b.lang] || DICT.id;
      const msg = d.review_invite_wa.replace("{name}", b.contact_name).replace("{link}", `${window.location.origin}/review/${data.token}`);
      const url = waLink(waPhone(b.phone), msg);
      if (win) win.location.href = url; else window.location.href = url;
      onDone({ ...b, review_token: data.token });
    } catch (e) { win?.close(); toast.error(formatErr(e)); }
  };
  return (
    <button data-testid={`${tid}-${b.code}`} onClick={ask} className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${b.status === "completed" ? "bg-[#25D366] text-white hover:bg-[#1da851]" : "border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
      <MessageCircle className="h-3.5 w-3.5" />{t("ask_review")}{b.review_token && " ✓"}
    </button>
  );
}

function Stats() {
  const { t } = useLang();
  const { data } = useApi("/admin/stats");
  if (!data) return null;
  const items = [[data.bookings, "admin_bookings"], [data.pending, "stat_pending"], [data.users, "stat_users"], [data.itineraries, "stat_itins"]];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
      {items.map(([v, k]) => <div key={k} data-testid={`stat-${k}`} className="rounded-2xl border border-slate-200 bg-white p-5"><p className="text-3xl font-extrabold text-[#064E3B]">{v}</p><p className="text-sm text-slate-500">{t(k)}</p></div>)}
    </div>
  );
}

function Table() {
  const { t } = useLang();
  const { data, setData } = useApi("/admin/bookings");
  const partners = useApi("/admin/partners-approved").data;
  const change = async (id, status) => {
    const booking = data.find((b) => b.id === id);
    if (["confirmed", "completed"].includes(status) && !["confirmed", "completed"].includes(booking.status)
        && !window.confirm(t("access_admin_confirm").replace("{phone}", booking.phone_normalized || booking.phone))) return;
    try { await http.patch(`/admin/bookings/${id}`, { status }); setData(data.map((b) => (b.id === id ? { ...b, status } : b))); toast.success(t(`status_${status}`)); }
    catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div className="space-y-3">
      <p data-testid="admin-phone-proof-warning" className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-relaxed text-amber-900">{t("access_admin_warning")}</p>
    <div className="overflow-x-auto rounded-3xl border border-slate-200 bg-white">
      <table className="w-full text-sm" data-testid="admin-bookings-table">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><tr>{["booking_code", "customer", "item", "date", "total", "status", "asg_partner", "nav_reviews"].map((k) => <th key={k} className="px-4 py-3">{t(k)}</th>)}</tr></thead>
        <tbody>
          {(data || []).map((b) => (
            <tr key={b.id} className="border-t border-slate-100" data-testid={`admin-row-${b.code}`}>
              <td className="px-4 py-3 font-mono text-xs">{b.code}</td>
              <td className="px-4 py-3"><p className="font-semibold">{b.contact_name}{b.guest && <span data-testid={`booking-guest-badge-${b.code}`} className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-800">{t("bk_guest")}</span>}</p><p className="text-xs text-slate-500">{b.phone}</p></td>
              <td className="px-4 py-3">{b.item_name}<p className="text-xs text-slate-500">{b.item_type} · {b.quantity}x{b.guide_name && ` · ${t("addon_guide_short")}: ${b.guide_name}`}</p>{b.transport_ref && <p data-testid={`admin-booking-transport-${b.code}`} className="text-xs font-semibold text-[#E11D48]">{t("transport_label")}: {b.transport_ref}</p>}{b.addons?.length > 0 && <p data-testid={`admin-booking-addons-${b.code}`} className="text-xs text-sky-800">+ {b.addons.map((a) => t(ADDON_KEYS[a])).join(", ")}</p>}</td>
              <td className="px-4 py-3">{b.date}</td>
              <td className="px-4 py-3 font-bold">{idr(b.total)}</td>
              <td className="px-4 py-3">
                <select data-testid={`admin-status-${b.code}`} value={b.status} onChange={(e) => change(b.id, e.target.value)} className={`rounded-full px-3 py-1 text-xs font-bold outline-none ${STATUS_CLS[b.status]}`}>
                  {STATUSES.map((s) => <option key={s} value={s}>{t(`status_${s}`)}</option>)}
                </select>
              </td>
              <td className="px-4 py-3"><AssignPartner b={b} partners={partners} onChange={(nb) => setData(data.map((x) => (x.id === nb.id ? nb : x)))} /></td>
              <td className="px-4 py-3"><AskReview b={b} onDone={(nb) => setData(data.map((x) => (x.id === nb.id ? nb : x)))} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </div>
  );
}

function Reminders({ onChange }) {
  const { t } = useLang();
  const { data, setData } = useApi("/admin/review-reminders");
  if (!data) return null;
  return (
    <div data-testid="admin-review-reminders" className="mb-8 rounded-3xl border border-amber-200 bg-amber-50 p-6">
      <h2 className="flex items-center gap-2 text-xl font-bold">{t("reminders_title")}<span data-testid="admin-reminders-count" className="rounded-full bg-[#E11D48] px-2.5 py-0.5 text-sm text-white">{data.length}</span></h2>
      <p className="mt-1 text-sm text-slate-600">{t("reminders_sub")}</p>
      {data.length === 0 ? <p className="mt-4 text-sm text-slate-500">{t("reminders_empty")}</p> : (
        <ul className="mt-4 divide-y divide-amber-200">
          {data.map((b) => (
            <li key={b.id} data-testid={`admin-reminder-${b.code}`} className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm">
              <div><p className="font-semibold">{b.contact_name} · <span className="font-mono text-xs">{b.code}</span></p><p className="text-xs text-slate-500">{b.item_name} · {(b.completed_at || b.date).slice(0, 10)} · {(b.lang || "id").toUpperCase()}</p></div>
              <AskReview b={b} tid="admin-reminder-ask" onDone={(nb) => { setData(data.map((x) => (x.id === nb.id ? nb : x))); onChange?.(nb); }} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const MEETING_POINTS = ["Masjid Raya Sumatera Barat, Padang", "Bandara Internasional Minangkabau (BIM)"];

function TripAdminCard({ trip, names, onSaved, payReady }) {
  const { t } = useLang();
  const [links, setLinks] = useState(Object.fromEntries(trip.participants.filter((p) => p.payment_url).map((p) => [p.id, p.payment_url])));
  const midtrans = async (p) => {
    try { const { data } = await http.post(`/admin/trip-requests/${trip.id}/participants/${p.id}/payment`); setLinks({ ...links, [p.id]: data.redirect_url }); toast.success(t("ot_link_ready")); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const [f, setF] = useState({ destinations: trip.destinations, final_price: trip.final_price || "", meeting_point: trip.meeting_point || MEETING_POINTS[0], payment_link: trip.payment_link || "" });
  const move = (i, d) => { const a = [...f.destinations]; [a[i], a[i + d]] = [a[i + d], a[i]]; setF({ ...f, destinations: a }); };
  const save = async (status) => {
    try {
      const { data } = await http.patch(`/admin/trip-requests/${trip.id}`, { ...f, final_price: f.final_price ? Number(f.final_price) : null, payment_link: f.payment_link || null, ...(status ? { status } : {}) });
      onSaved(data); toast.success(t("ot_saved"));
    } catch (e) { toast.error(formatErr(e)); }
  };
  const payMsg = (p) => (DICT[p.lang] || DICT.id).ot_pay_wa.replace("{name}", p.name).replace("{route}", f.destinations.map((d) => names[d] || d).join(" → "))
    .replace("{dates}", `${trip.date_from} – ${trip.date_to}`).replace("{price}", idr(Number(f.final_price) || 0)).replace("{meet}", f.meeting_point).replace("{link}", links[p.id] || f.payment_link || "-");
  const field = "rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none";
  return (
    <div data-testid={`admin-trip-${trip.code}`} className="rounded-2xl border border-slate-200 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-bold">{trip.code} <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold uppercase">{t(`ot_badge_${trip.status}`)}</span></p>
        <p className="text-sm text-slate-500">{trip.date_from} – {trip.date_to} · <b className="text-slate-900">{trip.total_pax}/{trip.min_pax}–{trip.max_pax}</b> pax</p>
      </div>
      <ol className="mt-3 space-y-1">
        {f.destinations.map((d, i) => (
          <li key={d} className="flex items-center gap-2 text-sm"><span className="w-5 text-slate-400">{i + 1}.</span>{names[d] || d}
            <button disabled={i === 0} onClick={() => move(i, -1)} data-testid={`admin-trip-up-${trip.code}-${i}`} className="ml-auto rounded px-2 text-slate-500 hover:bg-slate-100 disabled:opacity-30">↑</button>
            <button disabled={i === f.destinations.length - 1} onClick={() => move(i, 1)} className="rounded px-2 text-slate-500 hover:bg-slate-100 disabled:opacity-30">↓</button></li>
        ))}
      </ol>
      <div className="mt-3 grid gap-2 md:grid-cols-3">
        <input data-testid={`admin-trip-price-${trip.code}`} type="number" min={0} value={f.final_price} onChange={(e) => setF({ ...f, final_price: e.target.value })} placeholder={t("ot_final_price")} className={field} />
        <select data-testid={`admin-trip-meet-${trip.code}`} value={f.meeting_point} onChange={(e) => setF({ ...f, meeting_point: e.target.value })} className={field}>{MEETING_POINTS.map((m) => <option key={m}>{m}</option>)}</select>
        <input data-testid={`admin-trip-paylink-${trip.code}`} value={f.payment_link} onChange={(e) => setF({ ...f, payment_link: e.target.value })} placeholder={t("ot_pay_link")} className={field} />
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button onClick={() => save()} data-testid={`admin-trip-save-${trip.code}`} className="rounded-full border border-slate-300 px-4 py-1.5 text-xs font-bold hover:bg-slate-50">{t("ot_save")}</button>
        <button onClick={() => save("approved")} data-testid={`admin-trip-approve-${trip.code}`} className="rounded-full bg-[#064E3B] px-4 py-1.5 text-xs font-bold text-white hover:bg-[#047857]">{t("ot_approve")}</button>
        <button onClick={() => save("cancelled")} data-testid={`admin-trip-cancel-${trip.code}`} className="rounded-full px-4 py-1.5 text-xs font-bold text-rose-600 hover:bg-rose-50">{t("status_cancelled")}</button>
      </div>
      <ul className="mt-3 divide-y text-sm">
        {trip.participants.map((p) => (
          <li key={p.id} className="flex items-center justify-between gap-2 py-2">
            <span>{p.name} · {p.phone} · {p.country || "-"} · {p.pax} pax
              {p.payment_status && <b data-testid={`admin-trip-paystatus-${p.id}`} className={`ml-2 rounded-full px-2 py-0.5 text-[10px] uppercase ${p.payment_status === "paid" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{p.payment_status}</b>}</span>
            {trip.status === "approved" && payReady && p.payment_status !== "paid" && <button onClick={() => midtrans(p)} data-testid={`admin-trip-midtrans-${p.id}`} className="ml-auto rounded-full border border-slate-300 px-3 py-1 text-xs font-bold hover:bg-slate-50">{links[p.id] ? t("ot_regen_link") : t("ot_make_link")}</button>}
            {trip.status === "approved" && <a href={waLink(waPhone(p.phone, p.country), payMsg(p))} target="_blank" rel="noreferrer" data-testid={`admin-trip-paywa-${p.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#25D366] px-3 py-1 text-xs font-bold text-white"><MessageCircle className="h-3.5 w-3.5" />{t("ot_send_pay")}</a>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function progressMsg(tr, p, route) {
  const d = DICT[p.lang] || DICT.id;
  const left = tr.total_pax >= tr.min_pax ? d.ot_prog_full : d.ot_prog_left.replace("{n}", tr.min_pax - tr.total_pax);
  return `${d.ot_prog_wa.replace("{name}", p.name.split(" ")[0]).replace("{route}", route(tr)).replace("{dates}", `${tr.date_from} – ${tr.date_to}`)
    .replace("{count}", tr.total_pax).replace("{min}", tr.min_pax).replace("{left}", left)} ${window.location.origin}/open-trip#trip-${tr.code}`;
}

function ProgressAlerts({ trips, route, today, onSaved }) {
  const { t } = useLang();
  const due = trips.filter((tr) => ["pending", "ready"].includes(tr.status) && tr.date_from >= today)
    .map((tr) => ({ tr, people: tr.participants.filter((p) => (p.notified_pax ?? tr.total_pax) < tr.total_pax) }))
    .filter((x) => x.people.length);
  const mark = async (tr, p) => {
    try { const { data } = await http.post(`/admin/trip-requests/${tr.id}/participants/${p.id}/notified`); onSaved(data); }
    catch (e) { toast.error(formatErr(e)); }
  };
  if (!due.length) return null;
  return (
    <div data-testid="admin-progress-list" className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
      <p className="font-bold text-emerald-900">{t("ot_prog_title")} ({due.reduce((s, x) => s + x.people.length, 0)})</p>
      <p className="text-xs text-emerald-800">{t("ot_prog_sub")}</p>
      {due.map(({ tr, people }) => (
        <div key={tr.id} data-testid={`admin-progress-${tr.code}`} className="mt-3 text-sm">
          <p className="font-semibold">{tr.code} · {route(tr)} · {tr.total_pax}/{tr.min_pax} pax</p>
          <div className="mt-1 flex flex-wrap gap-2">{people.map((p) => (
            <a key={p.id} href={waLink(waPhone(p.phone, p.country), progressMsg(tr, p, route))} target="_blank" rel="noreferrer" onClick={() => mark(tr, p)} data-testid={`admin-progress-wa-${p.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#25D366] px-3 py-1 text-xs font-bold text-white transition-transform hover:-translate-y-0.5"><MessageCircle className="h-3.5 w-3.5" />{p.name}</a>
          ))}</div>
        </div>
      ))}
    </div>
  );
}

function TripRequests() {
  const { t } = useLang();
  const { data, setData } = useApi("/admin/trip-requests");
  const names = Object.fromEntries((useApi("/pois").data || []).map((p) => [p.slug, p.name]));
  const payCfg = useApi("/admin/payment-config").data;
  const today = new Date().toISOString().slice(0, 10);
  const h3 = new Date(Date.now() + 3 * 86400000).toISOString().slice(0, 10);
  const atRisk = (data || []).filter((tr) => tr.status === "pending" && tr.date_from >= today && tr.date_from <= h3);
  const route = (tr) => tr.destinations.map((d) => names[d] || d).join(" → ");
  const h3Msg = (tr, p) => (DICT[p.lang] || DICT.id).ot_h3_wa.replace("{name}", p.name).replace("{route}", route(tr)).replace("{dates}", `${tr.date_from} – ${tr.date_to}`);
  return (
    <div data-testid="admin-trip-requests" className="mt-10 rounded-3xl border border-slate-200 bg-white p-6">
      <h2 className="text-xl font-bold">Open Trip · {t("ot_admin_title")} <span className="text-sm font-medium text-slate-400">({(data || []).length})</span></h2>
      <p className="mt-1 text-sm text-slate-500">{t("ot_admin_sub")}</p>
      {payCfg && <p data-testid="admin-midtrans-status" className={`mt-3 rounded-2xl px-4 py-2 text-sm font-semibold ${payCfg.configured ? "bg-emerald-50 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>Midtrans · {payCfg.configured ? `${t("ot_mid_on")} (${payCfg.mode})` : t("ot_mid_off")}</p>}
      {atRisk.length > 0 && (
        <div data-testid="admin-h3-list" className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-4">
          <p className="font-bold text-rose-800">{t("ot_h3_title")} ({atRisk.length})</p>
          <p className="text-xs text-rose-700">{t("ot_h3_sub")}</p>
          {atRisk.map((tr) => (
            <div key={tr.id} data-testid={`admin-h3-${tr.code}`} className="mt-3 text-sm">
              <p className="font-semibold">{tr.code} · {route(tr)} · {tr.date_from} · {tr.total_pax}/{tr.min_pax} pax</p>
              <div className="mt-1 flex flex-wrap gap-2">{tr.participants.map((p) => (
                <a key={p.id} href={waLink(waPhone(p.phone, p.country), h3Msg(tr, p))} target="_blank" rel="noreferrer" data-testid={`admin-h3-wa-${p.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#25D366] px-3 py-1 text-xs font-bold text-white"><MessageCircle className="h-3.5 w-3.5" />{p.name}</a>
              ))}</div>
            </div>
          ))}
        </div>
      )}
      <ProgressAlerts trips={data || []} route={route} today={today} onSaved={(nt) => setData(data.map((x) => (x.id === nt.id ? nt : x)))} />
      <div className="mt-4 space-y-4">{(data || []).map((tr) => <TripAdminCard key={tr.id} trip={tr} names={names} payReady={!!payCfg?.configured} onSaved={(nt) => setData(data.map((x) => (x.id === nt.id ? nt : x)))} />)}</div>
    </div>
  );
}

function Reviews() {
  const { t } = useLang();
  const { data, setData } = useApi("/admin/reviews");
  const toggle = async (r) => {
    try { await http.patch(`/admin/reviews/${r.id}`, { hidden: !r.hidden }); setData(data.map((x) => (x.id === r.id ? { ...x, hidden: !r.hidden } : x))); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const remove = async (r) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try { await http.delete(`/admin/reviews/${r.id}`); setData(data.filter((x) => x.id !== r.id)); }
    catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div data-testid="admin-reviews" className="mt-10 rounded-3xl border border-slate-200 bg-white p-6">
      <h2 className="text-xl font-bold">{t("admin_reviews")} <span className="text-sm font-medium text-slate-400">({(data || []).length})</span></h2>
      <ul className="mt-4 divide-y">
        {(data || []).map((r) => (
          <li key={r.id} data-testid={`admin-review-${r.id}`} className={`flex items-start justify-between gap-4 py-4 text-sm ${r.hidden ? "opacity-50" : ""}`}>
            <div className="min-w-0">
              <p className="flex flex-wrap items-center gap-2 font-semibold">{r.user_name}
                <span className="flex items-center gap-0.5 text-amber-500"><Star className="h-3.5 w-3.5 fill-amber-400" />{r.rating}</span>
                <span className="text-xs text-slate-500">{r.destination_name} · {r.country || "-"} · {r.created_at.slice(0, 10)}</span>
                {r.sample && <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-extrabold uppercase text-slate-700">{t("sample")}</span>}
                {r.verified && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-extrabold uppercase text-emerald-800">{t("verified_booking")}</span>}
                {r.photos?.length > 0 && <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-extrabold uppercase text-sky-800">{r.photos.length} {t("photos")}</span>}
                {r.hidden && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-extrabold uppercase text-amber-800">{t("hidden")}</span>}
              </p>
              <p className="mt-1 text-slate-600">{r.comment}</p>
            </div>
            <div className="flex shrink-0 gap-1">
              <button data-testid={`admin-review-toggle-${r.id}`} title={r.hidden ? t("show") : t("hide")} onClick={() => toggle(r)} className="rounded-full p-2 text-slate-600 hover:bg-slate-100">{r.hidden ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}</button>
              <button data-testid={`admin-review-delete-${r.id}`} title={t("delete")} onClick={() => remove(r)} className="rounded-full p-2 text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Videos() {
  const { t } = useLang();
  const { data, setData } = useApi("/videos");
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [dest, setDest] = useState("");
  const [vlang, setVlang] = useState("id");
  const dests = useApi("/destinations").data || [];
  const destName = (s) => dests.find((d) => d.slug === s)?.name;
  const add = async (e) => {
    e.preventDefault();
    try { const { data: v } = await http.post("/admin/videos", { url, title, destination_slug: dest || null, lang: vlang }); setData([...(data || []), v]); setUrl(""); setTitle(""); setDest(""); setVlang("id"); toast.success(t("video_added")); }
    catch (err) { toast.error(formatErr(err)); }
  };
  const remove = async (id) => {
    try { await http.delete(`/admin/videos/${id}`); setData(data.filter((v) => v.id !== id)); }
    catch (err) { toast.error(formatErr(err)); }
  };
  return (
    <div data-testid="admin-videos" className="mt-10 rounded-3xl border border-slate-200 bg-white p-6">
      <h2 className="text-xl font-bold">{t("admin_videos")}</h2>
      <form onSubmit={add} className="mt-4 flex flex-col gap-3 md:flex-row">
        <input data-testid="admin-video-url-input" value={url} onChange={(e) => setUrl(e.target.value)} required placeholder="https://www.tiktok.com/@user/video/123..." className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 outline-none focus:border-[#064E3B]" />
        <input data-testid="admin-video-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("video_title_ph")} className="md:w-56 rounded-xl border border-slate-200 px-4 py-2.5 outline-none focus:border-[#064E3B]" />
        <select data-testid="admin-video-lang-select" value={vlang} onChange={(e) => setVlang(e.target.value)} title={t("video_lang")} className="md:w-36 rounded-xl border border-slate-200 bg-white px-3 py-2.5 outline-none">
          {LANGS.filter((l) => !l.text).map((l) => <option key={l.code} value={l.code}>{l.short} · {l.label}</option>)}
        </select>
        <select data-testid="admin-video-dest-select" value={dest} onChange={(e) => setDest(e.target.value)} className="md:w-60 rounded-xl border border-slate-200 bg-white px-3 py-2.5 outline-none">
          <option value="">{t("video_no_dest")}</option>
          {dests.map((d) => <option key={d.slug} value={d.slug}>{d.name}</option>)}
        </select>
        <button data-testid="admin-video-add-btn" className="flex items-center justify-center gap-1 rounded-full bg-[#064E3B] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#047857]"><Plus className="h-4 w-4" />{t("add_video")}</button>
      </form>
      <ul className="mt-4 divide-y">
        {(data || []).map((v) => (
          <li key={v.id} data-testid={`admin-video-${v.video_id}`} className="flex items-center justify-between gap-3 py-3 text-sm">
            <div className="min-w-0"><p className="font-semibold">{v.title || "TikTok"}{v.destination_slug && <span className="ml-2 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-bold text-[#064E3B]">{destName(v.destination_slug) || v.destination_slug}</span>}<span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold uppercase text-slate-600">{v.lang || "id"}</span></p><a href={v.url} target="_blank" rel="noreferrer" className="block truncate text-slate-500 hover:underline">{v.url}</a></div>
            <button data-testid={`admin-video-delete-${v.video_id}`} onClick={() => remove(v.id)} className="rounded-full p-2 text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Admin() {
  const [tick, setTick] = useState(0);
  const { user, ready } = useAuth();
  const { t } = useLang();
  if (!ready) return null;
  if (!user || user.role !== "admin") return <Navigate to="/login?next=/admin" replace />;
  return (
    <div data-testid="admin-page">
      <PageHero title={t("admin_title")} />
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8"><Stats /><Reminders onChange={() => setTick((n) => n + 1)} /><PickupList key={`p${tick}`} /><Table key={tick} /><TripRequests /><PartnerApps /><ChatInbox /><CityWaitlist /><CityPricing /><Reviews /><Videos /></section>
    </div>
  );
}
